/**
 * The read primitive every cabinet screen uses.
 *
 * WHY THIS EXISTS RATHER THAN TanStack Query, WHICH IS RULED. adr/0007 fixes "TanStack Router +
 * Query" for both SPAs, and `@tanstack/react-query` IS NOT INSTALLED — `web/authors/package.json`
 * depends on `@mantine/core`, `@mantine/hooks`, `@moonx/ui`, `react` and `react-dom`, and nothing
 * else. Adding a dependency is not this track's to do, so this hook stands in with the same
 * SHAPE — `{ status, data, error, refetch }` — and none of the caching. It is forty lines and it
 * is meant to be deleted: when the dependency lands, the screens change their import and their
 * destructuring stays.
 *
 * WHAT IT DOES DO PROPERLY, because getting these wrong is what makes a "simple" fetch hook a bug
 * factory:
 *   - it aborts on unmount and on a changed key, so a slow first read cannot overwrite a fast
 *     second one;
 *   - it ignores AbortError, so leaving a screen never paints an error state on the way out;
 *   - it keeps the LAST GOOD DATA while refetching, which is what makes a partial state possible:
 *     a stale table under a quiet "refreshing" line beats a table that blinks out to a spinner.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type AsyncStatus = 'loading' | 'ready' | 'error';

export interface AsyncResult<T> {
  readonly status: AsyncStatus;
  /** The last value that loaded. Present during a refetch, which is what makes `isRefreshing` useful. */
  readonly data: T | undefined;
  readonly error: unknown;
  /** True while a read is in flight AND a previous value is still on screen. */
  readonly isRefreshing: boolean;
  readonly refetch: () => void;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * `key` is what identifies the read. Change it and the previous read is aborted and replaced —
 * it is the dependency list, spelled as a value so a caller cannot forget one.
 */
export function useAsync<T>(
  read: (signal: AbortSignal) => Promise<T>,
  key: string,
): AsyncResult<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [status, setStatus] = useState<AsyncStatus>('loading');
  const [attempt, setAttempt] = useState(0);

  // The reader is captured in a ref so a caller may pass an inline arrow without re-triggering.
  // `key` is the only thing that identifies a read, deliberately.
  const readRef = useRef(read);
  readRef.current = read;

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    setStatus((current) => (current === 'ready' ? 'ready' : 'loading'));
    setError(undefined);

    readRef
      .current(controller.signal)
      .then((value) => {
        if (!live) {
          return;
        }
        setData(value);
        setStatus('ready');
      })
      .catch((cause: unknown) => {
        if (!live || isAbort(cause)) {
          return;
        }
        setError(cause);
        setStatus('error');
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [key, attempt]);

  const refetch = useCallback(() => {
    setAttempt((value) => value + 1);
  }, []);

  return {
    status,
    data,
    error,
    isRefreshing: status === 'loading' && data !== undefined,
    refetch,
  };
}
